import React from "react";
import { cn } from "~/lib/cn";
import { type VariantProps, cva } from "class-variance-authority";

export const badgeVariants = cva(
  "inline-flex items-center gap-1 border font-semibold transition focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        "outline-primary": "text-foreground border-primary text-purple-700",
        "outline-secondary": "border-gray-200 bg-brand-25 text-text-tertiary",
        gray: "border-none bg-gray-50 text-gray-800 hover:bg-gray-100",
        overlay:
          "border-transparent bg-gradient-brand from-opacity-60 to-opacity-60 backdrop-blur text-white",
        pro: "bg-gradient-pro-orange hover:brightness-110 active:brightness-90 text-white border-orange-500",
        affirm: "bg-lime-100 text-lime-700 border-none",
        danger: "bg-red-100 text-red-700 border-none",
        pending: "bg-blue-100 text-blue-700 border-none",
      },
      size: {
        sm: "px-2.5 py-0.5 text-xs leading-none",
        lg: "px-4 py-2 text-md leading-none",
        default: "px-3 py-2 text-sm leading-none",
      },
      radius: {
        full: "rounded-full",
        rounded: "rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "full",
    },
  }
);

type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants>;

export const Badge = ({
  className,
  variant,
  size,
  radius,
  children,
  ...props
}: BadgeProps) => {
  return (
    <div
      {...props}
      className={cn(badgeVariants({ variant, size, radius }), className)}
    >
      {children}
    </div>
  );
};
